import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type ConnectorSummary, type UserStructuredProps } from "../lib/api";
import { SourceForm, getMissingRequiredFields, type SourceFormValues } from "./SourceForm";
import {
  isStructuredPropertiesValid,
  StructuredPropertiesEditor,
} from "./StructuredPropertiesEditor";
import { litmusCronFromConfig, describeSchedule } from "./AddSourceDialog";
import { useToast } from "./toast/ToastProvider";

interface SourceFull {
  id: string;
  name: string;
  connectorId: string;
  config: Record<string, unknown>;
  structuredProperties?: UserStructuredProps;
  scheduleCron: string;
  enabled: boolean;
  includeRawAttachments: boolean;
}

interface EditSourceDialogProps {
  source: SourceFull;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// EditSourceDialog wraps SourceForm in editing mode. The store leaves
// secrets unchanged when an empty value comes through, so the operator
// only re-types secrets they actually want to rotate.
export function EditSourceDialog({ source, open, onOpenChange }: EditSourceDialogProps) {
  const qc = useQueryClient();
  const { show } = useToast();

  const connectors = useQuery({
    queryKey: ["connectors"],
    queryFn: () => api.get<ConnectorSummary[]>("/api/v1/connectors"),
    enabled: open,
  });
  const connector = connectors.data?.find((c) => c.id === source.connectorId);

  const [name, setName] = useState(source.name);
  const [scheduleCron, setScheduleCron] = useState(source.scheduleCron);
  const [enabled, setEnabled] = useState(source.enabled);
  const [values, setValues] = useState<SourceFormValues>({
    config: { ...source.config },
    secrets: {},
  });
  const [structuredProperties, setStructuredProperties] = useState<UserStructuredProps>(
    source.structuredProperties ?? {},
  );
  const [error, setError] = useState<string | null>(null);

  // For connectors in scheduled mode, recompute the cron expression whenever
  // the user changes sync_frequency or sync_time so the saved cron always
  // matches what the form shows.
  const isScheduledSync =
    (source.connectorId === "litmus" || source.connectorId === "postgres" || source.connectorId === "mssql") &&
    values.config["ingestion_mode"] === "scheduled";

  useEffect(() => {
    if (!isScheduledSync) return;
    const freq = (values.config["sync_frequency"] as string) ?? "daily";
    const time = (values.config["sync_time"] as string) ?? "02:00";
    const tz = (values.config["sync_timezone"] as string) ?? "UTC";
    const day = (values.config["sync_day"] as string) ?? "Sunday";
    const date = (values.config["sync_date"] as string) ?? "1";
    setScheduleCron(litmusCronFromConfig(freq, time, tz, day, date));
  }, [isScheduledSync, values.config["sync_frequency"], values.config["sync_time"], values.config["sync_timezone"], values.config["sync_day"], values.config["sync_date"]]);

  // Ensure sync_date / sync_day defaults are set when frequency changes.
  useEffect(() => {
    if (!isScheduledSync) return;
    const freq = (values.config["sync_frequency"] as string) ?? "daily";
    setValues((prev: SourceFormValues) => {
      if (freq === "monthly" && !prev.config["sync_date"])
        return { ...prev, config: { ...prev.config, sync_date: "1" } };
      if (freq === "weekly" && !prev.config["sync_day"])
        return { ...prev, config: { ...prev.config, sync_day: "Sunday" } };
      return prev;
    });
  }, [isScheduledSync, values.config["sync_frequency"]]);

  // Reset local state when the dialog opens for a new (or refetched) source.
  useEffect(() => {
    if (!open) return;
    setName(source.name);
    setScheduleCron(source.scheduleCron);
    setEnabled(source.enabled);
    setValues({ config: { ...source.config }, secrets: {} });
    setStructuredProperties(source.structuredProperties ?? {});
    setError(null);
  }, [
    open,
    source.id,
    source.name,
    source.scheduleCron,
    source.enabled,
    source.config,
    source.structuredProperties,
  ]);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/v1/sources/${encodeURIComponent(source.id)}`, {
        name: name.trim(),
        config: values.config,
        secrets: values.secrets, // empty values are skipped server-side
        structuredProperties,
        scheduleCron: scheduleCron.trim(),
        enabled,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sources"] });
      qc.invalidateQueries({ queryKey: ["sources", source.id] });
      qc.invalidateQueries({ queryKey: ["uploads"] });
      show("Source saved", `"${name.trim()}" updated`, "success");
      onOpenChange(false);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Save failed";
      setError(msg);
      show("Save failed", msg, "error");
    },
  });

  const scheduleDescription = isScheduledSync
    ? describeSchedule(
        (values.config["sync_frequency"] as string) ?? "daily",
        (values.config["sync_time"] as string) ?? "02:00",
        (values.config["sync_timezone"] as string) ?? "UTC",
        (values.config["sync_day"] as string) ?? "Sunday",
        (values.config["sync_date"] as string) ?? "1",
      )
    : "";

  function submit() {
    if (!connector) return;
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    const missing = getMissingRequiredFields(connector.schema, values, true);
    if (missing.length > 0) {
      setError(`Required: ${missing.join(", ")}`);
      return;
    }
    if (!isStructuredPropertiesValid(structuredProperties)) {
      setError("Fix the invalid or conflicting static metadata fields before continuing.");
      return;
    }
    setError(null);
    save.mutate();
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[1400] bg-ink/40 backdrop-blur-sm" />
        <Dialog.Content
          className="fixed left-1/2 top-[72px] z-[1401] w-full max-w-xl -translate-x-1/2 rounded-soft border 
          border-rule bg-surface p-7 shadow-cardLift max-h-[calc(100vh-96px)] overflow-y-auto"
        >
          {" "}
          <Dialog.Title asChild>
            <h2 className="font-display text-[22px] font-semibold tracking-tight text-ink">
              Edit {connector?.displayName ?? source.connectorId}
            </h2>
          </Dialog.Title>
          <Dialog.Description className="mt-1.5 mb-6 text-[13px] text-ash">
            Connector type can't be changed after creation. To switch connectors, delete this source
            and create a new one.
          </Dialog.Description>
          {!connector ? (
            connectors.isLoading ? (
              <p className="text-sm text-ash">Loading connector schema…</p>
            ) : (
              <p className="text-sm text-rose-700">
                Connector "{source.connectorId}" is no longer registered on this agent build.
              </p>
            )
          ) : (
            <div className="space-y-5">
              <label className="block">
                <span className="mb-1.5 block font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
                  Name
                </span>
                <input value={name} onChange={(e) => setName(e.target.value)} className="input" />
              </label>

              <SourceForm
                schema={connector.schema}
                value={values}
                onChange={setValues}
                connectorId={connector.id}
                editing
              />

              <StructuredPropertiesEditor
                value={structuredProperties}
                onChange={setStructuredProperties}
              />

              {scheduleDescription ? (
                <p className="text-[12.5px] leading-relaxed text-ash">{scheduleDescription}</p>
              ) : null}

              <label className="flex items-center gap-2 hidden">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  className="h-4 w-4 rounded border-rule text-brand-600"
                />
                <span className="text-[13px] text-ink">Enabled</span>
                <span className="text-[12px] text-ash">
                  - when off, scheduled crawls are skipped (manual refresh still works).
                </span>
              </label>

              {error ? (
                <div className="rounded-md border border-rose-500/30 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
                  {error}
                </div>
              ) : null}

              <div className="flex justify-end gap-2 border-t border-rule pt-5">
                <button onClick={() => onOpenChange(false)} className="btn-secondary">
                  Cancel
                </button>
                <button onClick={submit} disabled={save.isPending} className="btn-primary">
                  {save.isPending ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
